import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Copy, FileText, Eye, Loader2 } from "lucide-react";
import {
  ANRSelectionModal,
  CopyTemplateModal,
  DeleteConfirmModal,
} from "./modals";
import { fetchTemplates, createTemplate, deleteTemplate } from "./api";
import TemplateCard from "./TemplateCard";
import ProcessLegend from "./ProcessLegend";
import TemplateCalendar from "./TemplateCalendar";

// Main Component
const TemplateProjectPlanner = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTemplates, setExpandedTemplates] = useState({});
  const [legendVisible, setLegendVisible] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Modal states
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [templateToCopy, setTemplateToCopy] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [anrModalOpen, setAnrModalOpen] = useState(false);
  const [templateForANR, setTemplateForANR] = useState(null);

  // Fetch templates on mount
  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const templatesData = await fetchTemplates();
      setTemplates(templatesData);
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error("Fehler beim Laden der Templates");
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (templateId) => {
    setExpandedTemplates((prev) => ({
      ...prev,
      [templateId]: !prev[templateId],
    }));
  };

  // Copy Template
  const handleCopyClick = (template) => {
    setTemplateToCopy(template);
    setCopyModalOpen(true);
  };

  const handleCopyTemplate = async (newName) => {
    if (!templateToCopy) return;

    const payload = {
      name: newName,
      processes: templateToCopy.processes.map((process) => ({
        id: process.id,
        label: process.label,
        hours: process.hours,
        names: process.names || [],
      })),
    };

    try {
      const response = await createTemplate(payload);
      if (response.status === 201) {
        const newTemplate = response.data.template;
        setTemplates((prev) => [...prev, newTemplate]);
        toast.success("Template erfolgreich kopiert!");
      } else {
        toast.error("Fehler beim Kopieren des Templates.");
      }
    } catch (error) {
      console.error("Error copying template:", error);
      toast.error("Ein Fehler ist aufgetreten.");
    } finally {
      setCopyModalOpen(false);
      setTemplateToCopy(null);
    }
  };

  // Delete Template
  const handleDeleteClick = (template) => {
    setTemplateToDelete(template);
    setDeleteModalOpen(true);
  };

  const handleDeleteTemplate = async () => {
    if (!templateToDelete) return;

    try {
      const response = await deleteTemplate(templateToDelete._id);
      if (response.status === 200) {
        setTemplates((prev) =>
          prev.filter((t) => t._id !== templateToDelete._id)
        );
        toast.success("Template erfolgreich gelöscht!");
      } else {
        toast.error("Fehler beim Löschen des Templates.");
      }
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error("Ein Fehler ist aufgetreten.");
    } finally {
      setDeleteModalOpen(false);
      setTemplateToDelete(null);
    }
  };

  // Assign ANR
  const handleAssignANRClick = (template) => {
    setTemplateForANR(template);
    setAnrModalOpen(true);
  };

  const handleSelectANR = (anrData) => {
    if (!templateForANR) return;

    setTemplates((prev) =>
      prev.map((t) =>
        t._id === templateForANR._id
          ? {
              ...t,
              assignedANR: {
                anr: anrData.anr,
                auftragsbezeichnung: anrData.auftragsbezeichnung,
                maxLohnaufwand: anrData.maxLohnaufwand,
                projectId: anrData.projectId,
              },
            }
          : t
      )
    );

    toast.success(`ANR ${anrData.anr} erfolgreich zugewiesen!`);
    setTemplateForANR(null);
  };

  // Handle template selection for calendar preview
  const handleTemplateSelect = (template) => {
    setSelectedTemplate(
      selectedTemplate?._id === template._id ? null : template
    );
  };

  // Handle date selection from calendar
  const handleDateSelect = (dateInfo) => {
    console.log("Date selected:", dateInfo);
    // Can be extended for scheduling functionality
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div className="w-96 bg-white border-r border-gray-200 flex flex-col h-full overflow-hidden flex-shrink-0">
        {/* Sidebar Header */}
        <div className="p-4 border-b border-gray-200">
          <h1 className="text-xl font-bold text-gray-900">Template Projekt Planer</h1>
          <p className="text-sm text-gray-500 mt-1">
            {templates.length} Template{templates.length !== 1 ? "s" : ""}{" "}
            verfügbar
          </p>
        </div>

        {/* Templates List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              <span className="ml-2 text-gray-500">Laden...</span>
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Keine Templates vorhanden</p>
            </div>
          ) : (
            templates.map((template) => (
              <div
                key={template._id}
                className={`cursor-pointer transition-all ${
                  selectedTemplate?._id === template._id
                    ? "ring-2 ring-blue-500 rounded-lg"
                    : ""
                }`}
                onClick={() => handleTemplateSelect(template)}
              >
                <TemplateCard
                  template={template}
                  onCopy={handleCopyClick}
                  onDelete={handleDeleteClick}
                  onAssignANR={handleAssignANRClick}
                  expandedTemplates={expandedTemplates}
                  toggleExpand={toggleExpand}
                />
              </div>
            ))
          )}
        </div>

        {/* Process Legend */}
        <div className="p-4 border-t border-gray-200">
          <ProcessLegend
            templates={templates}
            isVisible={legendVisible}
            onToggle={() => setLegendVisible(!legendVisible)}
          />
        </div>
      </div>

      {/* Calendar Area */}
      <div className="flex-1 p-4 overflow-hidden relative">
        <TemplateCalendar
          selectedTemplate={selectedTemplate}
          onDateSelect={handleDateSelect}
        />
      </div>

      {/* Modals */}
      <CopyTemplateModal
        open={copyModalOpen}
        onClose={() => {
          setCopyModalOpen(false);
          setTemplateToCopy(null);
        }}
        onCopy={handleCopyTemplate}
        templateName={templateToCopy?.name}
      />

      <DeleteConfirmModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setTemplateToDelete(null);
        }}
        onConfirm={handleDeleteTemplate}
        templateName={templateToDelete?.name}
      />

      <ANRSelectionModal
        open={anrModalOpen}
        onClose={() => {
          setAnrModalOpen(false);
          setTemplateForANR(null);
        }}
        onSelect={handleSelectANR}
      />
    </div>
  );
};

export default TemplateProjectPlanner;
